import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SEO } from '../../utils/seo';

export default function ProfilePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: '橘穂',
    jobTitle: '算命学占星術認定鑑定士',
    description: '算命学十三代宗家系譜 算命学占星術認定鑑定士。正看護師として病院勤務をしていた中で、人生の波の違いに疑問を抱き、その答えを紐解いてくれる学問として算命学と出会いました。',
    image: 'https://static.readdy.ai/image/b84ab4ce2ae3714b9b5e2bb5be24c7fc/5c742ae525d0e33c93b3ced6ac937702.jpeg',
    worksFor: {
      '@type': 'Organization',
      name: '橘穂の算命学カウンセリング',
    },
    url: `${siteUrl}/profile`,
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track scroll position to toggle nav background/shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /**
   * Navigate to a specific section on the home page.
   * Uses `navigate` to change the URL hash.
   * Errors are caught and logged to avoid breaking the UI.
   */
  const goToHomeSection = (sectionId: string) => {
    try {
      setIsMobileMenuOpen(false);
      // Ensure the hash starts with '#'
      const hash = sectionId.startsWith('#') ? sectionId : `#${sectionId}`;
      navigate(`/${hash}`);
    } catch (error) {
      console.error('Failed to navigate to home section:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F1E8] to-white overflow-x-hidden">
      <SEO
        title="プロフィール｜橘穂の算命学カウンセリング"
        description="算命学十三代宗家系譜 算命学占星術認定鑑定士 橘穂（きつほ）のプロフィール。正看護師として病院勤務をしていた中で、人生の波の違いに疑問を抱き、その答えを紐解いてくれる学問として算命学と出会いました。現在も師範の元に通い、日々学びを深めながら、お客様一人ひとりの人生のライフステージを丁寧に読み解いております。"
        keywords="橘穂,きつほ,算命学鑑定士,プロフィール,認定鑑定士,正看護師"
        canonical={`${siteUrl}/profile`}
        ogTitle="プロフィール｜橘穂の算命学カウンセリング"
        ogDescription="算命学十三代宗家系譜 算命学占星術認定鑑定士 橘穂（きつほ）のプロフィール。お客様一人ひとりの人生のライフステージを丁寧に読み解いております。"
        schema={schema}
      />

      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-[#F5F1E8]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center space-x-2 sm:space-x-3 cursor-pointer min-w-0"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/dab67413-5a70-4602-b752-4997924ca2b6.png"
                alt="橘穂の算命学カウンセリング ロゴ"
                className="w-9 h-9 sm:w-12 sm:h-12 flex-shrink-0"
              />
              <span className="text-sm sm:text-lg font-serif text-[#8B4513] whitespace-nowrap truncate">
                橘穂の算命学カウンセリング
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-10">
              <Link
                to="/"
                className="text-sm font-light tracking-wide transition-colors whitespace-nowrap text-[#2C2C2C] hover:text-[#6B8E7F]"
              >
                ホーム
              </Link>
              <Link
                to="/about"
                className="text-sm font-light tracking-wide transition-colors whitespace-nowrap text-[#2C2C2C] hover:text-[#6B8E7F]"
              >
                算命学とは
              </Link>
              <Link
                to="/profile"
                className="text-sm font-light tracking-wide transition-colors whitespace-nowrap text-[#6B8E7F]"
              >
                プロフィール
              </Link>
              <button
                onClick={() => goToHomeSection('menu')}
                className="text-sm font-light tracking-wide transition-colors whitespace-nowrap text-[#2C2C2C] hover:text-[#6B8E7F] cursor-pointer"
              >
                メニュー
              </button>
              <button
                onClick={() => goToHomeSection('contact')}
                className="text-sm font-light tracking-wide transition-colors whitespace-nowrap text-[#2C2C2C] hover:text-[#6B8E7F] cursor-pointer"
              >
                ご予約
              </button>
            </div>

            <div className="hidden lg:block">
              <button
                onClick={() => goToHomeSection('campaign')}
                className="px-5 py-2 rounded-full border border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white transition-all whitespace-nowrap cursor-pointer"
              >
                <span className="text-sm mr-1">初回</span>
                <span className="text-sm">特別鑑定</span>
              </button>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-[#2C2C2C] cursor-pointer"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg border-t border-[#E0E0E0]">
            <div className="px-4 py-4 space-y-1">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg"
              >
                ホーム
              </Link>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg"
              >
                算命学とは
              </Link>
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-light text-[#6B8E7F] hover:bg-[#F5F1E8] rounded-lg"
              >
                プロフィール
              </Link>
              <button
                onClick={() => goToHomeSection('menu')}
                className="block w-full text-left px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg cursor-pointer"
              >
                メニュー
              </button>
              <button
                onClick={() => goToHomeSection('contact')}
                className="block w-full text-left px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg cursor-pointer"
              >
                ご予約
              </button>
              <div className="pt-2 px-4">
                <button
                  onClick={() => goToHomeSection('campaign')}
                  className="w-full py-3 rounded-full border border-[#2C2C2C] text-[#2C2C2C] text-sm font-light hover:bg-[#2C2C2C] hover:text-white transition-all whitespace-nowrap cursor-pointer"
                >
                  初回特別鑑定
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#2C2C2C] mb-4 sm:mb-6 leading-relaxed"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            プロフィール
          </h1>
          <p className="text-[#8B7355] text-xs sm:text-sm font-light">
            算命学十三代宗家系譜 算命学占星術認定鑑定士
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 sm:py-20 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl p-6 sm:p-10 lg:p-12 shadow-sm">
            <div className="prose prose-lg max-w-none">
              <div className="text-center mb-8 sm:mb-12">
                <div className="mb-6 sm:mb-8">
                  <img
                    src="https://static.readdy.ai/image/b84ab4ce2ae3714b9b5e2bb5be24c7fc/5c742ae525d0e33c93b3ced6ac937702.jpeg"
                    alt="橘穂プロフィール写真"
                    className="w-36 h-36 sm:w-44 sm:h-44 rounded-full object-cover object-top mx-auto shadow-sm border-4 border-[#F5F1E8]"
                  />
                </div>
                <h2
                  className="text-2xl sm:text-3xl font-light text-[#2C2C2C] mb-4"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  橘穂（きつほ）
                </h2>
                <p className="text-[#8B7355] text-xs sm:text-sm font-light">
                  算命学十三代宗家系譜 算命学占星術認定鑑定士
                </p>
              </div>

              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-6 sm:mb-8">
                はじめまして<br />
                算命学占星術認定鑑定士の橘穂（きつほ）と申します<br />
                ホームページをご覧頂きありがとうございます
              </p>

              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-6 sm:mb-8">
                算命学と出会う前、私は正看護師として病院勤務をしていました。<br />
                様々なバックグラウンドを持つ多くの患者様と接し、治療、療養に携わりながら常日頃疑問に思う事がありました。
              </p>

              <div className="bg-[#F5F1E8] rounded-xl p-5 sm:p-8 mb-6 sm:mb-8">
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8B7355] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      高度な医療を受けているのにも関わらずなかなか子宝に恵まれない
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8B7355] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      身体が弱く病気を繰り返す
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8B7355] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      何故か続けてお子様に先立たれてしまう
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8B7355] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      ご家族との関係があまり良くなく孤独な入院生活を送っている
                    </span>
                  </li>
                </ul>
              </div>

              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-6 sm:mb-8">
                その様な方々がいる一方で
              </p>

              <div className="bg-[#E8F0ED] rounded-xl p-5 sm:p-8 mb-6 sm:mb-8">
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#6B8E7F] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      ご高齢であるが病気らしい病気をした事がない
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#6B8E7F] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      ピンチの時には必ず誰かが手を差し伸べてくれる
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#6B8E7F] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      お子様達は伸び伸びと育ち、家庭環境も良い
                    </span>
                  </li>
                </ul>
              </div>

              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-4">
                どなたの人生にも波があります。<br />
                人生の荒波の多い人、少ない人の差があるのは何故なのか？<br />
                努力だけではどうにもならないこの流れは何だろう？
              </p>

              <p className="text-[#6B8E7F] text-base sm:text-lg font-light leading-loose mb-8 sm:mb-12 text-center">
                その疑問を紐解いてくれる学問が算命学でした。
              </p>

              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-8 sm:mb-12">
                算命学の学びは深く、終わりがありません。<br />
                現在も師範の元に通い、日々学びを深めております。
              </p>

              <div className="bg-gradient-to-br from-[#E8F0ED] to-[#F5F1E8] rounded-2xl p-6 sm:p-10 mb-6 sm:mb-8">
                <h3
                  className="text-xl sm:text-2xl font-light text-[#2C2C2C] mb-4 sm:mb-6 text-center"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  鑑定時大切にしていること
                </h3>

                <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-4 sm:mb-6">
                  お客様のご相談内容をお聞きしながら、現在、人生のどのライフステージに立っていらっしゃるのかを読み解いていきます。
                </p>

                <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-4 sm:mb-6">
                  置かれている現状やお気持ちに寄り添いながら分かりやすく丁寧にお伝えいたします。
                </p>

                <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose">
                  最初は何が悩みなのか言葉にできなくても大丈夫です。<br />
                  小さな事でもお気軽にご連絡ください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#2C2C2C] mb-4 sm:mb-6 leading-relaxed"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            あなたの人生に、<br />
            新しい視点を。
          </h2>
          <p className="text-[#8B7355] text-sm sm:text-base font-light mb-8 sm:mb-10">
            初回特別鑑定、受付中です
          </p>
          <button
            onClick={() => goToHomeSection('contact')}
            className="inline-flex items-center gap-3 px-8 sm:px-10 py-3 sm:py-4 bg-[#6B8E7F] text-white rounded-full text-sm font-light tracking-wider hover:bg-[#5A7D6E] transition-colors whitespace-nowrap cursor-pointer"
          >
            <span>鑑定を申し込む</span>
            <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#8B4513] text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/dab67413-5a70-4602-b752-4997924ca2b6.png"
                  alt="橘穂の算命学カウンセリング ロゴ"
                  className="w-8 h-8 sm:w-10 sm:h-10"
                />
                <span className="text-sm sm:text-base font-serif whitespace-nowrap">
                  橘穂の算命学カウンセリング
                </span>
              </div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                サービス
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <Link
                    to="/"
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap"
                  >
                    ホーム
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap"
                  >
                    算命学とは
                  </Link>
                </li>
                <li>
                  <Link
                    to="/profile"
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap"
                  >
                    プロフィール
                  </Link>
                </li>
                <li>
                  <button
                    onClick={() => goToHomeSection('menu')}
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    鑑定メニュー
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                ご予約
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <span className="text-xs sm:text-sm font-light cursor-pointer hover:text-white/80 transition-colors whitespace-nowrap">
                    営業時間
                  </span>
                </li>
                <li>
                  <button
                    onClick={() => goToHomeSection('contact')}
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    予約方法
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                ご利用条件
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <span className="text-xs sm:text-sm font-light cursor-pointer hover:text-white/80 transition-colors whitespace-nowrap">
                    女性限定について
                  </span>
                </li>
                <li>
                  <span className="text-xs sm:text-sm font-light cursor-pointer hover:text-white/80 transition-colors whitespace-nowrap">
                    対面鑑定エリア
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                お問い合わせ
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <button
                    onClick={() => goToHomeSection('contact')}
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    メールフォーム
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img
                src="https://public.readdy.ai/ai/img_res/0ef220cc-5e3a-48f0-bc37-23fb3250621c.png"
                alt="橘穂の算命学カウンセリング"
                className="h-7 w-7 sm:h-8 sm:w-8 object-contain"
              />
              <div>
                <div className="text-sm sm:text-lg font-light">
                  橘穂の算命学カウンセリング
                </div>
                <p className="text-[10px] sm:text-xs font-light mt-1">
                  女性限定・完全予約制
                </p>
              </div>
            </div>

            <div className="text-center sm:text-right">
              <p className="text-[10px] sm:text-xs font-light mb-2">
                © 2025 All Rights Reserved
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] sm:text-xs font-light hover:text-white/80 transition-colors whitespace-nowrap"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
